import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, MenuItem,
  Switch, Tooltip, IconButton, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import axios from '../../api/client';

// The sources an other-income receipt can be filed under.
//
// Every agency charges for different things, so this is a table rather than a list in the
// code. The seeded rows are a starting point; what an agency actually charges for is theirs
// to decide, and this screen is where they decide it.
//
// THE CATEGORY IS WHAT CHOOSES THE ACCOUNT. That is the whole reason this is an admin screen
// and not a free-text box on the receipt form: somebody who knows the chart of accounts picks
// where each kind of income lands, once, and the front desk then only has to say what the
// money was for.
const emptyForm = { name: '', description: '', accountCode: '', sortOrder: 100 };

const IncomeCategoryManagement = () => {
  const [rows, setRows] = useState([]);
  const [incomeAccounts, setIncomeAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      // all=true so retired categories are listed too — they can be switched back on.
      const res = await axios.get('/other-income/categories?all=true');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load income categories');
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/accounting/chart-of-accounts');
        const all = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        // Only income accounts. The server refuses anything else, so offering more would only
        // produce a rejection somebody has to work out for themselves.
        setIncomeAccounts(all.filter((a) => a.type === 'income'));
      } catch (e) { /* the picker degrades to the server default */ }
    };
    load();
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      accountCode: row.accountCode || '',
      sortOrder: row.sortOrder ?? 100
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Give the category a name');

    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/other-income/categories/${editing._id}`, {
          name: form.name.trim(),
          description: form.description,
          accountCode: form.accountCode || undefined,
          sortOrder: Number(form.sortOrder) || 100
        });
        toast.success(`'${form.name.trim()}' updated`);
      } else {
        await axios.post('/other-income/categories', {
          name: form.name.trim(),
          description: form.description,
          accountCode: form.accountCode || undefined,
          sortOrder: Number(form.sortOrder) || 100
        });
        toast.success(`'${form.name.trim()}' added`);
      }
      setOpen(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save this category');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.put(`/other-income/categories/${row._id}`, { isActive: !row.isActive });
      toast.success(row.isActive ? `'${row.name}' retired` : `'${row.name}' back in use`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not change this category');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove '${row.name}'? This cannot be undone.`)) return;
    try {
      const { data } = await axios.delete(`/other-income/categories/${row._id}`);
      toast.success(data.message);
      fetchRows();
    } catch (e) {
      // The server refuses a category with receipts behind it and says to retire it instead.
      toast.error(e.response?.data?.message || 'Could not remove this category');
    }
  };

  const accountLabel = (code) => {
    const acc = incomeAccounts.find((a) => a.code === code);
    return acc ? `${acc.code} ${acc.name}` : code;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h5">Income Categories</Typography>
          <Typography variant="body2" color="textSecondary">
            What the agency charges for, and which account each one posts to.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add Source</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Changing where a category posts affects future receipts only. Everything already
        recorded keeps the account it actually used.
      </Alert>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Source</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Posts to</TableCell>
            <TableCell align="right">Receipts</TableCell>
            <TableCell align="center">In use</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id} hover>
              <TableCell>
                {row.name}
                {row.isOther && (
                  <Tooltip title="What the form offers when nothing else fits. It asks the user to type the source.">
                    <Chip size="small" label="Catch-all" sx={{ ml: 1 }} variant="outlined" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">{row.description || '—'}</Typography>
              </TableCell>
              <TableCell>{accountLabel(row.accountCode)}</TableCell>
              <TableCell align="right">{row.receiptCount ?? 0}</TableCell>
              <TableCell align="center">
                <Tooltip title={row.isSystem
                  ? 'A system category cannot be retired — the receipt form needs it'
                  : (row.isActive ? 'Retire this source' : 'Put it back in use')}>
                  <span>
                    <Switch size="small" checked={!!row.isActive} disabled={row.isSystem}
                      onChange={() => toggleActive(row)} />
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title={row.isSystem
                  ? 'A system category cannot be removed'
                  : (row.receiptCount > 0
                    ? `Used by ${row.receiptCount} receipt(s) — retire it instead`
                    : 'Remove')}>
                  <span>
                    <IconButton size="small" disabled={row.isSystem || row.receiptCount > 0}
                      onClick={() => handleDelete(row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No categories yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit '${editing.name}'` : 'Add a Source of Income'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                helperText="What staff will see on the receipt form, for example Parking fee" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                helperText="Optional. Says when to use this one." />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField select fullWidth label="Posts to" value={form.accountCode}
                onChange={(e) => setForm({ ...form, accountCode: e.target.value })}
                helperText="Income accounts only. Leave blank for Sundry Agency Income.">
                <MenuItem value="">Sundry Agency Income (default)</MenuItem>
                {incomeAccounts.map((a) => (
                  <MenuItem key={a._id || a.code} value={a.code}>{a.code} {a.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Order" value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                helperText="Lower shows first" />
            </Grid>
            {editing?.isSystem && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  This is a system category. It can be renamed and re-pointed, but not retired
                  or removed, because the receipt form falls back to it.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default IncomeCategoryManagement;
